// Canvas Size command — changes the document canvas size, optionally
// repositioning layers via an anchor point. Expanding adds transparent
// space; cropping clips layers outside the new bounds.

import type { Command } from "./Command";
import type { Document } from "../document/Document";
import type { RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { Selection } from "../selection/Selection";

export type Anchor =
  | "top-left" | "top" | "top-right"
  | "left" | "center" | "right"
  | "bottom-left" | "bottom" | "bottom-right";

export interface CanvasSizeOptions {
  width: number;
  height: number;
  anchor: Anchor;
  fillColor?: { r: number; g: number; b: number; a: number };
}

export class ResizeCanvasCommand implements Command {
  readonly kind = "resize-canvas";

  constructor(
    private readonly options: CanvasSizeOptions,
    private before?: { width: number; height: number },
  ) {}

  apply(doc: Document): Document {
    const { width: newW, height: newH, anchor } = this.options;

    if (!this.before) {
      this.before = { width: doc.width, height: doc.height };
    }

    const oldW = doc.width;
    const oldH = doc.height;

    // Calculate offset based on anchor
    let dx = 0, dy = 0;
    switch (anchor) {
      case "top-left": dx = 0; dy = 0; break;
      case "top": dx = Math.round((newW - oldW) / 2); dy = 0; break;
      case "top-right": dx = newW - oldW; dy = 0; break;
      case "left": dx = 0; dy = Math.round((newH - oldH) / 2); break;
      case "center": dx = Math.round((newW - oldW) / 2); dy = Math.round((newH - oldH) / 2); break;
      case "right": dx = newW - oldW; dy = Math.round((newH - oldH) / 2); break;
      case "bottom-left": dx = 0; dy = newH - oldH; break;
      case "bottom": dx = Math.round((newW - oldW) / 2); dy = newH - oldH; break;
      case "bottom-right": dx = newW - oldW; dy = newH - oldH; break;
    }

    // Shift all layer positions
    for (const layer of collectRasters(doc.layers)) {
      layer.x += dx;
      layer.y += dy;
    }

    // #69: translate doc.selection by (dx, dy) and resize the mask buffer to
    // match the new canvas. Pixels that move off-canvas are clipped.
    (doc as { selection: Selection | null }).selection = translateAndResizeSelection(
      doc.selection, oldW, oldH, newW, newH, dx, dy,
    );

    doc.width = newW;
    doc.height = newH;
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    if (!this.before) throw new Error("Cannot invert without before state");
    return new ResizeCanvasCommand({
      width: this.before.width,
      height: this.before.height,
      anchor: this.options.anchor,
    }, { width: _after.width, height: _after.height });
  }
}

function collectRasters(layers: readonly (import("../document/Layer").Layer)[]): import("../document/Layer").RasterLayer[] {
  const result: import("../document/Layer").RasterLayer[] = [];
  for (const l of layers) {
    if (l.kind === "raster") result.push(l);
    else if (l.kind === "group") result.push(...collectRasters(l.children));
  }
  return result;
}

/**
 * Translate a selection mask by (dx, dy) and resize the buffer to (newW, newH).
 * Pixels that move off-canvas are clipped. (#69)
 */
function translateAndResizeSelection(
  sel: Selection | null,
  oldW: number,
  oldH: number,
  newW: number,
  newH: number,
  dx: number,
  dy: number,
): Selection | null {
  if (!sel || sel.isEmpty()) {
    return sel ? Selection.empty(newW, newH) : null;
  }
  if (newW <= 0 || newH <= 0) return Selection.empty(Math.max(0, newW), Math.max(0, newH));
  const next = new Uint8Array(newW * newH);
  // For each pixel in the new mask, find the corresponding source pixel.
  for (let y = 0; y < newH; y++) {
    const sy = y - dy;
    if (sy < 0 || sy >= oldH) continue;
    for (let x = 0; x < newW; x++) {
      const sx = x - dx;
      if (sx < 0 || sx >= oldW) continue;
      next[y * newW + x] = sel.mask[sy * oldW + sx] ?? 0;
    }
  }
  return new Selection(newW, newH, next);
}
